import React, { useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Breadcrumb, BreadcrumbItem, BucketContentExplorer } from "sprint-common-web";
import {
    isObjectFile,
    isObjectFolder,
    BucketFile,
    BucketObject
} from "sprint-common-web/src/components/explorer/BucketObject";

import { mapToBucketObject } from "../../../common/api/delivery-api/BucketInfoResponse";
import { GetDeliveriesBucketFilesQuery } from "../../../common/api/delivery-api/GetDeliveriesBucketFilesQuery";
import { SearchDeliveriesBucketQuery } from "../../../common/api/delivery-api/SearchDeliveriesBucketQuery";
import { useDeliveryApi } from "../../../common/api/delivery-api/useDeliveryApi";
import { OriginatorsDataProvider } from "../../../common/data/lookup-data-providers/OriginatorsDataProvider";
import { PageHeader } from "../../../common/ui/layout/header/PageHeader";
import { ProcessBucketFilesDialog } from "../../common/ProcessBucketFilesDialog";
import { getDeliveryBucketListingHref } from "../../ContentIngestionRoutes";

export const DeliveryBucketExplorer: React.VFC = () => {
    const history = useHistory();

    const {
        getDeliveriesBucketFiles,
        abortGetDeliveriesBucketFilesRequest,
        createBucketDelivery,
        searchDeliveriesBucket
    } = useDeliveryApi();

    const { deliveryBucketId, deliveryBucketName, bucketUniqueId } = useParams<{
        deliveryBucketId: string;
        deliveryBucketName: string;
        bucketUniqueId: string;
    }>();

    const [selectedFiles, setSelectedFiles] = useState<BucketFile[]>([]);
    const [isProcessingDialogOpen, setIsProcessingDialogOpen] = useState<boolean>(false);

    const goToDeliveryBucketListing = () => {
        const href = getDeliveryBucketListingHref();
        history.push(href);
    };

    const openConfirmationDialog = () => {
        setIsProcessingDialogOpen(true);
    };

    const closeConfirmationDialog = () => {
        setIsProcessingDialogOpen(false);
    };

    const handleObjectsSelection = (selectedObjects: BucketObject[]) => {
        if (selectedObjects.some(isObjectFolder)) {
            throw new Error("Folder should not be selected.");
        }

        setSelectedFiles(selectedObjects.filter(isObjectFile));
        openConfirmationDialog();
    };

    const handleObjectSelection = (selectedObject: BucketObject) => {
        handleObjectsSelection([selectedObject]);
    };

    const handleProcessBucketFile = async (file: BucketFile) => {
        await createBucketDelivery({
            filePath: file.path,
            deliveryBucketId
        });
    };

    const handleProcessingComplete = () => {
        window.location.reload();
        closeConfirmationDialog();
    };

    const handleSearchBucket = async (query: SearchDeliveriesBucketQuery): Promise<BucketObject[]> => {
        /* if search is performed when `getDeliveriesBucketFiles()` request is still pending, 
            then displayed search results would be overridden when this request completed */
        abortGetDeliveriesBucketFilesRequest();

        const response = await searchDeliveriesBucket(query);

        return (response.content ?? []).map(mapToBucketObject);
    };

    const handleOpenFolder = async (query: GetDeliveriesBucketFilesQuery): Promise<BucketObject[]> => {
        const response = await getDeliveriesBucketFiles(query);

        return (response?.content ?? []).map(mapToBucketObject);
    };

    const items: BreadcrumbItem[] = [
        {
            key: "content-ingestion",
            text: "Content Ingestion"
        },
        {
            key: "delivery-buckets",
            text: "Delivery Buckets",
            onClick: goToDeliveryBucketListing
        },
        {
            key: "delivery-bucket",
            text: bucketUniqueId
        }
    ];

    return (
        <OriginatorsDataProvider>
            <PageHeader>
                <Breadcrumb items={items} />
            </PageHeader>
            <BucketContentExplorer
                bucketUniqueId={bucketUniqueId}
                selection={{
                    selectMany: {
                        text: "Upload files",
                        icon: "BulkUpload",
                        onClick: handleObjectsSelection
                    }
                }}
                onFileClick={handleObjectSelection}
                onFolderClick={handleOpenFolder}
                onSearch={handleSearchBucket}
                bucketName={deliveryBucketName}
            />
            {isProcessingDialogOpen && (
                <ProcessBucketFilesDialog
                    title="Upload to Sprint"
                    message={
                        "You are about to upload files listed below to Sprint. Files will be removed from bucket and will appear on ingest list after small delay."
                    }
                    selectedFiles={selectedFiles}
                    onProcessFile={handleProcessBucketFile}
                    onProcessingComplete={handleProcessingComplete}
                />
            )}
        </OriginatorsDataProvider>
    );
};
